import React, { useState, useEffect } from "react";
import { X, RefreshCw, Copy, Check, Info, Trash2, Cpu, Key, Lock, ShieldCheck, Tag, Globe } from "lucide-react";
import baseapi from "../../../services/baseapi";
import integrationapi from "../../../services/integrationapi";
import { showError, showSuccess } from "../../../utils/toast";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useClientLevel } from "../../../hooks/useClientLevel";

interface ExternalApiSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onUninstall: () => void;
    integrationData?: any;
}

const ExternalApiSettings: React.FC<ExternalApiSettingsProps> = ({
    isOpen,
    onClose,
    onUninstall,
    integrationData
}) => {
    const level = useClientLevel();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [keyPrefix, setKeyPrefix] = useState<string>("");
    const [newCredentials, setNewCredentials] = useState<any>(null);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [isRotating, setIsRotating] = useState(false);

    const parsedData = integrationData ? (
        typeof integrationData.integration_data === 'string'
            ? JSON.parse(integrationData.integration_data)
            : integrationData.integration_data
    ) : null;

    const fetchStatus = async () => {
        try {
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get("integrations/external-api/status", { params });
            if (response.data.status) {
                setKeyPrefix(response.data.data.api_key_prefix);
            }
        } catch (error) {
            console.error("Failed to fetch integration status:", error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchStatus();
            setNewCredentials(null);
        }
    }, [isOpen]);

    const handleRotateKey = async () => {
        setIsRotating(true);
        try {
            const response = await integrationapi.get("external/keys/rotate");
            if (response.data.status) {
                setNewCredentials(response.data.data);
                showSuccess("Credentials rotated successfully. Store them securely.");
                fetchStatus();
            } else {
                showError("Failed to rotate credentials");
            }
        } catch (error: any) {
            console.error("Connect integrationapi rotate failed, trying fallback...", error);
            try {
                const payload: any = {};
                if (level && level.name !== "global" && level.id) {
                    payload.client_id = level.id;
                }
                const response = await baseapi.post("integrations/external-api/rotate-key", payload);
                if (response.data.status) {
                    setNewCredentials(response.data.data);
                    showSuccess("Credentials rotated successfully. Store them securely.");
                    fetchStatus();
                    return;
                }
            } catch (fallbackError: any) {
                console.error("Fallback rotate failed:", fallbackError);
            }
            showError(error.response?.data?.message || "Failed to rotate credentials");
        } finally {
            setIsRotating(false);
        }
    };

    const handleUninstall = async () => {
        setIsConfirmOpen(false);
        try {
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await integrationapi.delete("integrations/external-api/uninstall", { params });
            if (response.data.status) {
                showSuccess("Integration uninstalled successfully");
                onUninstall();
                onClose();
            } else {
                showError("Failed to uninstall");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to uninstall");
        }
    };

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300 p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative bg-white dark:bg-[#0F172A] rounded-[2rem] border border-gray-200 dark:border-white/5 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300 flex flex-col">
                {/* Premium Header */}
                <div className="bg-gray-50 dark:bg-gradient-to-b dark:from-[#1E293B] dark:to-[#0F172A] px-6 py-5 flex items-center justify-between shrink-0 border-b border-gray-200 dark:border-white/5 shadow-sm dark:shadow-xl">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-orange-500/10 dark:bg-white/5 border border-orange-500/20 dark:border-white/10 flex items-center justify-center shrink-0">
                            <Cpu className="text-orange-500 w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900 dark:text-white tracking-tight uppercase">API Configuration</h2>
                            <p className="text-gray-500 dark:text-zinc-500 text-[9px] font-black uppercase tracking-[0.2em] leading-none mt-1.5 ">
                                Active Session
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-white transition-all p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 border border-transparent hover:border-gray-200 dark:hover:border-white/10 cursor-pointer"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                    {newCredentials ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            <div className="bg-orange-50 dark:bg-orange-500/10 border-l-[3px] border-orange-500 p-4 rounded-r-2xl flex items-center gap-3">
                                <Info className="text-orange-500 shrink-0" size={18} />
                                <p className="text-orange-950 dark:text-orange-200 text-[10px] font-bold leading-relaxed">
                                    New credentials generated. Existing keys are now deprecated. Please update your external systems immediately.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {/* API Key */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 dark:text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Key size={12} className="text-orange-500" /> NEW API KEY
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={newCredentials.api_key}
                                            className="w-full pl-3 pr-10 py-3 bg-gray-100 dark:bg-black/40 text-orange-600 dark:text-orange-400 rounded-xl font-mono text-[9px] border border-gray-200 dark:border-white/5 shadow-inner truncate"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(newCredentials.api_key, 'key')}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10 text-gray-700 dark:text-white rounded-lg transition-all border border-gray-200 dark:border-white/5 shadow-sm cursor-pointer"
                                        >
                                            {copiedField === 'key' ? <Check size={12} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={12} className="text-gray-400 dark:text-zinc-500" />}
                                        </button>
                                    </div>
                                </div>

                                {/* API Secret */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 dark:text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Lock size={12} className="text-orange-500" /> NEW API SECRET
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={newCredentials.api_secret}
                                            className="w-full pl-3 pr-10 py-3 bg-gray-100 dark:bg-black/40 text-orange-600 dark:text-orange-400 rounded-xl font-mono text-[9px] border border-gray-200 dark:border-white/5 shadow-inner truncate"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(newCredentials.api_secret, 'secret')}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10 text-gray-700 dark:text-white rounded-lg transition-all border border-gray-200 dark:border-white/5 shadow-sm cursor-pointer"
                                        >
                                            {copiedField === 'secret' ? <Check size={12} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={12} className="text-gray-400 dark:text-zinc-500" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {integrationData?.editable === false && (
                                <div className="p-3.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl text-blue-700 dark:text-blue-400 text-xs flex items-center gap-2.5">
                                    <Globe className="w-4 h-4 shrink-0" />
                                    <span>This global integration is managed centrally. Settings are read-only.</span>
                                </div>
                            )}
                            <div className="space-y-4">
                                {/* Stats Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Key Summary */}
                                    <div className="group p-4 bg-gray-50 dark:bg-black/20 rounded-3xl border border-gray-200 dark:border-white/5 hover:border-orange-500/20 transition-all shadow-inner overflow-hidden relative flex flex-col justify-between">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 blur-3xl -mr-12 -mt-12 group-hover:bg-orange-500/10 transition-all" />
                                        <div className="relative z-10">
                                            <span className="text-[10px] text-gray-500 dark:text-zinc-500 font-bold uppercase tracking-widest flex items-center gap-1.5 mb-2">
                                                <ShieldCheck size={12} className="text-emerald-600 dark:text-emerald-500" /> Active Key
                                            </span>
                                            <span className="text-xs font-black text-orange-600 dark:text-orange-500 font-mono tracking-[0.2em]">
                                                {keyPrefix || parsedData?.key_hint || "••••••••"}
                                            </span>
                                        </div>
                                        {integrationData?.editable !== false && (
                                            <button
                                                onClick={handleRotateKey}
                                                disabled={isRotating}
                                                className="mt-3 w-full py-2 bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200 rounded-xl transition-all font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm dark:shadow-xl active:scale-95 disabled:opacity-50 relative z-10 cursor-pointer"
                                            >
                                                <RefreshCw size={12} className={isRotating ? "animate-spin" : ""} />
                                                Rotate
                                            </button>
                                        )}
                                    </div>

                                    {/* Endpoint Display */}
                                    <div className="group p-4 bg-gray-50 dark:bg-black/20 rounded-3xl border border-gray-200 dark:border-white/5 hover:border-orange-500/20 transition-all shadow-inner overflow-hidden relative flex flex-col justify-between">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 blur-3xl -mr-12 -mt-12 group-hover:bg-blue-500/10 transition-all" />
                                        <div className="relative z-10 overflow-hidden">
                                            <label className="text-[10px] font-black text-gray-500 dark:text-zinc-500 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                                                <Globe size={12} className="text-orange-500" /> Endpoint
                                            </label>
                                            <div className="truncate text-orange-600 dark:text-orange-400 font-mono text-[10px] bg-white dark:bg-black/40 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-white/5">
                                                {localStorage.getItem('request_host') || window.location.origin}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(localStorage.getItem('request_host') || window.location.origin, 'url')}
                                            className="mt-3 w-full py-2 bg-white hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10 text-gray-700 dark:text-white rounded-xl transition-all border border-gray-200 dark:border-white/5 font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 relative z-10 cursor-pointer shadow-sm"
                                        >
                                            {copiedField === 'url' ? <Check size={12} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={12} className="text-gray-400 dark:text-zinc-400" />}
                                            {copiedField === 'url' ? 'Copied' : 'Copy URL'}
                                        </button>
                                    </div>
                                </div>

                                {/* Scopes Section */}
                                {parsedData?.scopes && parsedData.scopes.length > 0 && (
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 dark:text-zinc-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <Tag size={12} className="text-orange-500" /> Assigned Permissions
                                        </label>
                                        <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/5 shadow-inner">
                                            {parsedData.scopes.map((scope: string) => (
                                                <span key={scope} className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 text-[8.5px] font-black uppercase tracking-wider rounded-lg">
                                                    {scope.replace(':', ' ')}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Security Note */}
                                <div className="bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 p-4 rounded-2xl">
                                    <p className="text-[9px] text-gray-500 dark:text-zinc-500 font-medium leading-relaxed italic text-center">
                                        These keys allow high-level access to system resources. Rotate them regularly and never share them across insecure channels.
                                    </p>
                                </div>

                                {integrationData?.editable !== false && (
                                    <button
                                        onClick={() => setIsConfirmOpen(true)}
                                        className="w-full py-3.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/5 dark:hover:bg-rose-500/10 text-rose-600 dark:text-rose-500 rounded-2xl transition-all flex items-center justify-center gap-3 border border-rose-200 dark:border-rose-500/10 group active:scale-[0.98] cursor-pointer"
                                    >
                                        <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                                        <span className="uppercase tracking-[.2em] text-[10px] font-black">Disconnect Integration</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-[#0F172A] border-t border-gray-200 dark:border-white/5">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-gray-900 hover:bg-gray-800 dark:bg-[#1E293B] dark:hover:bg-[#334155] text-white font-bold rounded-xl transition-all border border-gray-900 dark:border-white/5 text-[10px] uppercase tracking-[0.2em] active:scale-[0.98] cursor-pointer"
                    >
                        Close Portal
                    </button>
                </div>
            </div>

            <ConfirmDialog
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleUninstall}
                title="Disable Integration"
                message="Are you sure you want to disconnect this API integration? All active external services using these credentials will lose access immediately."
                confirmText="Disconnect"
                isDestructive={true}
            />
        </div>
    );
};

export default ExternalApiSettings;
